import { useCallback, useEffect, useState } from "react";

import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceContext } from "@v2/contexts/WorkspaceDashboardContext";
import {
  v2CustomDomainRouter,
  v2CustomDomainUtils,
} from "@v2/trpc/routes/v2CustomDomain";

interface EntriCallbackData {
  domain: string | null;
  success: boolean;
  setupType:
    | "automatic"
    | "manual"
    | "sharedLogin"
    | "semiautomatic"
    | "purchase"
    | null;
  provider: string;
  lastStatus: string;
  error?: {
    code: string;
    title: string;
    details: string;
  };
}

interface EntriCloseEvent extends CustomEvent {
  detail: EntriCallbackData;
}

const useAddDomainModal = () => {
  const [addDomainModalOpen, setAddDomainModalOpen] = useState(false);
  const [isEntriModalInProgress, setIsEntriModalInProgress] = useState(false);

  const { mutate: createPendingDomain } =
    v2CustomDomainRouter.createPendingCustomDomain.useMutation({
      onSuccess: () => {
        // Invalidate the domains query to refetch and show the new pending domain
        void v2CustomDomainUtils.getByProjectId.invalidate({
          projectId,
        });
        setAddDomainModalOpen(false);
      },
    });

  const { workspace } = useCurrentWorkspaceContext();
  const { projectId } = useCurrentProjectContext();

  const handleOnEntriClose = useCallback(
    (event: EntriCloseEvent) => {
      const { domain, success, provider, lastStatus, error } = event.detail;

      if (success && domain && projectId) {
        void v2CustomDomainUtils.getByProjectId.invalidate({
          projectId,
        });
        if (!workspace) {
          return;
        }

        createPendingDomain({
          projectId,
          customDomain: domain,
          workspaceId: workspace.id,
        });
      } else {
        console.error("Domain setup failed:", {
          domain,
          provider,
          lastStatus,
          error,
        });
      }

      setIsEntriModalInProgress(false);
    },
    [projectId, workspace, createPendingDomain],
  );

  useEffect(() => {
    window.addEventListener(
      "onEntriClose",
      handleOnEntriClose as EventListener,
    );

    return () => {
      window.removeEventListener(
        "onEntriClose",
        handleOnEntriClose as EventListener,
      );
    };
  }, [handleOnEntriClose]);

  const openAddDomainModal = useCallback(() => {
    setAddDomainModalOpen(true);
  }, []);

  return {
    addDomainModalOpen,
    setAddDomainModalOpen,
    openAddDomainModal,
    isEntriModalInProgress,
  };
};

export default useAddDomainModal;
